/* eslint-disable max-len */
import React, { useCallback, useMemo } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Accordion } from "@xfrontend/xui-react-plus";
import BotServiceCard from "./BotServiceCard";
import { DashboardItem } from "#/apis/infra/getDashboard";
import { RISK_LEVEL_MAP, getScoreColor } from "#/types/xecguard";

const RISK_I18N_KEY: Record<string, string> = {
  4: "risk.high",
  3: "risk.medium",
  2: "risk.low",
  1: "risk.minimal",
};

const POLICY_TAG_ORDER: ReadonlyArray<string> = [
  "policyTag.systemPrompt",
  "policyTag.promptAttack",
  "policyTag.harmfulContent",
  "policyTag.piiAndSensitive",
  "policyTag.contentBias",
  "policyTag.customPolicy",
];

const DashboardAccordion = styled(Accordion)`
  .accordion-header {
    .label {
      font-size: 20px;
      font-weight: 700;
    }
  }

  .accordion-header:not(:has(.collapsed)) {
    &:after {
      content: "";
      display: block;
      width: 100%;
      height: 1px;
      background-color: var(--x-bg-300);
    }
  }

  .accordion-item {
    border-radius: 8px;
    overflow: hidden;
    background-color: transparent;
    border: none;
    margin-bottom: 12px;
  }

  .accordion-body {
    background: var(--x-bg-600);
  }

  .accordion-button {
    background: var(--x-bg-600);
    box-shadow: none;
    border-radius: 0px !important;

    &:after {
      /* eslint-disable max-len */
      background-image: url("data:image/svg+xml;charset=utf-8;base64,PHN2ZyB3aWR0aD0nMTcnIGhlaWdodD0nMTcnIHZpZXdCb3g9JzAgMCAxNyAxNycgZmlsbD0nbm9uZScgeG1sbnM9J2h0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnJz48cGF0aCBmaWxsLXJ1bGU9J2V2ZW5vZGQnIGNsaXAtcnVsZT0nZXZlbm9kZCcgZD0nTS44NSAxMC4yYzAtLjQ3LjM4LS44NS44NS0uODVoNS4xYy40NyAwIC44NS4zOC44NS44NXY1LjFhLjg1Ljg1IDAgMCAxLTEuNyAwdi00LjI1SDEuN2EuODUuODUgMCAwIDEtLjg1LS44NXpNMTAuMi44NWMuNDcgMCAuODUuMzguODUuODV2NC4yNWg0LjI1YS44NS44NSAwIDAgMSAwIDEuN2gtNS4xYS44NS44NSAwIDAgMS0uODUtLjg1VjEuN2MwLS40Ny4zOC0uODUuODUtLjg1eicgZmlsbD0nI0Y2RjZGNycvPjxwYXRoIGZpbGwtcnVsZT0nZXZlbm9kZCcgY2xpcC1ydWxlPSdldmVub2RkJyBkPSdNMTYuNzUxLjI0OWEuODUuODUgMCAwIDEgMCAxLjIwMmwtNS45NSA1Ljk1YS44NS44NSAwIDEgMS0xLjIwMi0xLjIwMmw1Ljk1LTUuOTVhLjg1Ljg1IDAgMCAxIDEuMjAyIDB6TTcuNDAxIDkuNTk5YS44NS44NSAwIDAgMSAwIDEuMjAybC01Ljk1IDUuOTVhLjg1Ljg1IDAgMSAxLTEuMjAyLTEuMjAybDUuOTUtNS45NWEuODUuODUgMCAwIDEgMS4yMDIgMHonIGZpbGw9JyNGNkY2RjcnLz48L3N2Zz4g");
      /* eslint-enable max-len */
    }

    &.collapsed {
      &:after {
        /* eslint-disable max-len */
        background-image: url("data:image/svg+xml;charset=utf-8;base64,PHN2ZyB3aWR0aD0nMTcnIGhlaWdodD0nMTcnIHZpZXdCb3g9JzAgMCAxNyAxNycgZmlsbD0nbm9uZScgeG1sbnM9J2h0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnJz48cGF0aCBmaWxsLXJ1bGU9J2V2ZW5vZGQnIGNsaXAtcnVsZT0nZXZlbm9kZCcgZD0nTTEwLjIuODVjMC0uNDcuMzgtLjg1Ljg1LS44NWg1LjFjLjQ3IDAgLjg1LjM4Ljg1Ljg1djUuMWEuODUuODUgMCAxIDEtMS43IDBWMS43aC00LjI1YS44NS44NSAwIDAgMS0uODUtLjg1ek0uODUgMTAuMmMuNDcgMCAuODUuMzguODUuODV2NC4yNWg0LjI1YS44NS44NSAwIDAgMSAwIDEuN0guODVhLjg1Ljg1IDAgMCAxLS44NS0uODV2LTUuMWMwLS40Ny4zOC0uODUuODUtLjg1eicgZmlsbD0nI0Y2RjZGNycvPjxwYXRoIGZpbGwtcnVsZT0nZXZlbm9kZCcgY2xpcC1ydWxlPSdldmVub2RkJyBkPSdNMTYuNzUxLjI0OWEuODUuODUgMCAwIDEgMCAxLjIwMmwtNS45NSA1Ljk1QS44NS44NSAwIDAgMSA5LjYgNi4ybDUuOTUtNS45NWEuODUuODUgMCAwIDEgMS4yMDIgMHpNNy40MDEgOS41OTlhLjg1Ljg1IDAgMCAxIDAgMS4yMDJsLTUuOTUgNS45NUEuODUuODUgMCAwIDEgLjI0OSAxNS41NWw1Ljk1LTUuOTVhLjg1Ljg1IDAgMCAxIDEuMjAyIDB6JyBmaWxsPScjRjZGNkY3Jy8+PC9zdmc+IA==");
        /* eslint-enable max-len */
      }
    }
  }
`;

const Divider = styled.div`
  height: 1px;
  background-color: var(--x-gray-700);
  width: 100%;
  margin: 0;
`;

interface BotServiceSectionProps {
  data: DashboardItem[];
  activeKey: string[];
  setActiveKey: (key: string[]) => void;
}

const BotServiceSection: React.FC<BotServiceSectionProps> = ({ data, activeKey, setActiveKey }) => {
  const { t } = useTranslation("dashboard");
  const { t: tCommon } = useTranslation("common");
  /* eslint-disable @typescript-eslint/naming-convention */
  const divisions = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const grouped: Record<string, any[]> = {};

    data.forEach((item) => {
      const division = item.division || t("uncategorized");
      if (!grouped[division]) grouped[division] = [];

      const s = item.summary;

      // Convert daywise_data to trend chart format
      // d.date is a plain date string "YYYY-MM-DD"; parse directly to avoid timezone shift
      const trends = (item.daywise_data ?? []).map((d) => {
        const [yyyy, mm, dd] = d.date.split("-");
        // Append T00:00 so Date parses as local time, not UTC
        const weekday = new Date(`${d.date}T00:00`).toLocaleDateString("en-US", { weekday: "short" });
        return {
          label: weekday,
          tooltipLabel: `${yyyy}/${mm}/${dd}`,
          total: d.request_count ?? 0,
          unsafe: d.request_unsafe_count ?? 0,
          custom: d.request_custom_unsafe_count ?? 0,
        };
      });
      const maxCount = trends.length > 0
        ? Math.max(...trends.map((t) => Math.max(t.total, t.unsafe)))
        : 0;
      const axisMax = maxCount > 10 ? maxCount + 10 : 20;

      grouped[division].push({
        id: item.service_token_name,
        title: item.service_token_name,
        headerStats: {
          p95Latency: s.p95_response_time_ms != null
            ? `${s.p95_response_time_ms.toFixed(1)}ms`
            : "0ms",
          maxLatency: s.max_response_time_ms != null
            ? `${s.max_response_time_ms.toFixed(1)}ms`
            : "0ms",
        },
        score: {
          value: Math.round(s.security_score ?? 0),
          sourceColor: getScoreColor(Math.round(s.security_score ?? 0)).color,
          sourcePieColor: getScoreColor(Math.round(s.security_score ?? 0)).color,
          target: 100,
          targetPieColor: "#4F4F4F",
        },
        stats: ["4", "3", "2", "1"].map((risk) => ({
          label: RISK_I18N_KEY[risk] ? tCommon(RISK_I18N_KEY[risk]) : `Risk ${risk}`,
          value: s.unsafe_request_counts_by_max_risk?.[risk] || 0,
          bg: RISK_LEVEL_MAP[risk]?.color || "#999",
        })),
        threatTrends: {
          series: [
            {
              name: t("chartSeries.totalRequests"),
              type: "line",
              data: trends.map((tr) => tr.total),
            },
            {
              name: t("chartSeries.policyViolations"),
              type: "column",
              data: trends.map((tr) => tr.unsafe),
            },
            {
              name: t("chartSeries.customPolicyViolations"),
              type: "column",
              data: trends.map((tr) => tr.custom),
            },
          ],
          categories: trends.map((tr) => tr.label),
          tooltipLabels: trends.map((tr) => tr.tooltipLabel),
          colors: ["#FFA6A6", "#846CE6", "#8E86A3"],
          config: {
            scoreAxis: { min: 0, max: 100, tickAmount: 5 },
            amountAxis: { min: 0, max: axisMax, tickAmount: 4 },
          },
        },
        inputUnsafe: s.request_unsafe_count || 0,
        outputUnsafe: s.violation_type_counts?.VIOLATION_CUSTOMIZED_RULE || 0,
        requests: {
          stats: {
            requests: s.request_count || 0,
            apiErrors: s.total_session_count || 0,
            mcpCalls: s.tool_request_count || 0,
          },
          series: [
            s.violation_type_counts?.VIOLATION_SYSTEM_PROMPT || 0,
            s.violation_type_counts?.VIOLATION_GENERAL_PROMPT || 0,
            s.violation_type_counts?.VIOLATION_HARMFUL || 0,
            s.violation_type_counts?.VIOLATION_PII || 0,
            s.violation_type_counts?.VIOLATION_CONTENT_BIASES || 0,
            s.violation_type_counts?.VIOLATION_CUSTOMIZED_RULE || 0,
          ],
          labels: POLICY_TAG_ORDER.map((key) => tCommon(key)),
          colors: [
            "#ADA9B9",
            "#9791A6",
            "#807A93",
            "#6A6280",
            "#5A536D",
            "#4A455A",
          ],
          total: s.request_count || 0,
        },
      });
    });

    return grouped;
  }, [data, t, tCommon]);
  /* eslint-enable @typescript-eslint/naming-convention */

  const accordionHeaderClick = useCallback((key: string) => {
    const newActiveKey = activeKey.includes(key) ? activeKey.filter((k) => k !== key) : [...activeKey, key];
    setActiveKey(newActiveKey);
    localStorage.setItem("dashboardAccordionActiveKey", JSON.stringify(newActiveKey));
  }, [activeKey]);

  return (
    <DashboardAccordion activeKey={activeKey} alwaysOpen>
      {Object.entries(divisions).map(([divisionName, services]) => (
        <Accordion.Item eventKey={divisionName} key={divisionName}>
          <Accordion.Header onClick={() => accordionHeaderClick(divisionName)}>
            <div className="label">{divisionName === t("uncategorized") ? t("groupHeader.uncategorized") : t("groupHeader.named", { name: divisionName })}</div>
          </Accordion.Header>
          <Accordion.Body>
            {services.map((service, index) => (
              <React.Fragment key={service.id}>
                <BotServiceCard data={service} />
                {index < services.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </Accordion.Body>
        </Accordion.Item>
      ))}
    </DashboardAccordion>
  );
};

export default BotServiceSection;
